// Words for the Augment page (screens/augment/AugmentScreen.tsx), kept apart so they can be tested.
import type { AugmentEstimate, AugmentJob } from "../api/augment";

// Copies per image are not limited (customer feedback, Oliver: it was 10); the server caps a run at 5,000 images.
export const JOB_POLL_MS = 1500;

const mb = (bytes: number) => (bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.max(0.1, bytes / 1e6).toFixed(1)} MB`);

/** "3 reviewed images × 4 copies = 12 new images", then cost and storage, each a line. */
export function estimateLines(e: AugmentEstimate): string[] {
  if (e.source_images === 0) return ["No reviewed images here yet: review some annotations first, then come back."];
  const lines = [`${e.source_images} reviewed image${e.source_images === 1 ? "" : "s"} × ${e.copies} cop${e.copies === 1 ? "y" : "ies"} = ${e.total} new image${e.total === 1 ? "" : "s"}`];
  if (e.metered && e.credits !== undefined) {
    lines.push(`Cost: ${e.total} × ${e.credits_per_image} credit${e.credits_per_image === 1 ? "" : "s"} = ${e.credits} credits (you have ${e.balance}). You pay only for the images actually made.`);
  }
  const storage = e.storage_allowance
    ? `Storage: about ${mb(e.bytes_estimate)} more, ${mb(e.storage_used + e.bytes_estimate)} of your ${mb(e.storage_allowance)}`
    : `Storage: about ${mb(e.bytes_estimate)} more`;
  lines.push(e.storage_fits ? storage : `${storage}: too much. Choose fewer images or copies.`);
  if (e.too_many) lines.push(`One run makes at most ${(e.max_total ?? 0).toLocaleString("en")} images: choose fewer images or copies, and start another run for more.`);
  return lines;
}

/** Whether the estimate allows starting. */
export const canStart = (e: AugmentEstimate | null) =>
  !!e && e.total > 0 && e.storage_fits && !e.too_many && (!e.metered || e.credits === undefined || (e.balance ?? 0) >= e.credits);

/** One run in words: what it is doing or how it ended. */
export function jobLine(j: AugmentJob): string {
  const noun = j.kind === "background" ? "background picture" : "image";
  const what = `${j.done} of ${j.total} ${noun}${j.total === 1 ? "" : "s"}`;
  switch (j.status) {
    case "queued": return "Starting…";
    case "running": return j.cancel_requested ? `Stopping after the current image… (${what})` : `Making images… ${what}`;
    case "completed": return j.message || `Done: ${j.done} ${noun}${j.done === 1 ? "" : "s"} in ${j.folder}`;
    default: return j.message || `${j.status === "cancelled" ? "Cancelled" : "Failed"} after ${what}`;
  }
}

export const isActive = (j: AugmentJob | null | undefined) => !!j && (j.status === "queued" || j.status === "running");

/** Whether an image in the gallery is an augmented copy (a generated background picture is one too: see isGeneratedBackground). */
export const isAugmented = (img: { augment_job_id?: number | null; provenance?: Record<string, unknown> | null }) =>
  img.augment_job_id != null || img.provenance?.source === "augmented";

/** Whether an image is a generated background picture: a copy saved as Empty with its objects erased. */
export const isGeneratedBackground = (img: { status?: string; augment_job_id?: number | null; provenance?: Record<string, unknown> | null }) =>
  img.provenance?.source === "background" || (img.status === "empty" && img.augment_job_id != null);
